import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { msalInstance, msalSettings } from "msalInstance";
import { instanceFlask } from "api/axiosConfig";

const AuthContext = createContext();
const AUTH_STORAGE_KEY = "document_builder_sso_session";

const parseMsalError = (error) => {
  const errorCode = error?.errorCode || error?.code || "unknown_error";
  const message = error?.errorMessage || error?.message || "Unknown SSO error";
  return `${errorCode}: ${message}`;
};

const buildUserFromAccount = (account) => {
  const fallbackName = account?.username ? account.username.split("@")[0] : "User";
  return {
    name: account?.name || fallbackName,
    email: account?.username || ""
  };
};

/**
 * Azure AD (MSAL) authentication, mirroring the `rmp` app's AuthContext.
 * This is a single-screen app with no router, so there is no deep-link
 * restore logic here - just sign-in state and a Bearer token available to
 * axios via msalInstance.acquireTokenSilent.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [appAccessDenied, setAppAccessDenied] = useState(null);
  const [session, setSession] = useState({ isAuthenticated: false, user: null });

  const msalClient = msalInstance;

  // Listen for backend 401 responses raised by the axios interceptor below.
  useEffect(() => {
    const handler = (e) => {
      setAppAccessDenied({
        message: e.detail?.message || "Access denied"
      });
    };
    window.addEventListener("app:auth-error", handler);
    return () => window.removeEventListener("app:auth-error", handler);
  }, []);

  // Verify the MSAL-authenticated user against the backend (confirms the
  // token is accepted and the email domain is allowed).
  const verifyBackendAccess = useCallback(
    async (account) => {
      try {
        const tokenResponse = await msalClient.acquireTokenSilent({
          scopes: msalSettings.scopes,
          account
        });
        await instanceFlask.get("/user/me", {
          headers: { Authorization: "Bearer " + tokenResponse.accessToken }
        });
        return { ok: true };
      } catch (error) {
        const status = error?.response?.status;
        const msg = error?.response?.data?.message;

        if (status) {
          return {
            ok: false,
            message: msg || "Your account is not authorized to access this application."
          };
        }

        // Network error (no response at all) — let the user through so the
        // app can function; individual API calls will surface errors later.
        console.warn("Backend verification failed (network error, non-blocking)", error);
        return { ok: true };
      }
    },
    [msalClient]
  );

  useEffect(() => {
    const initializeAuth = async () => {
      if (!msalClient) {
        setAuthError("SSO is not configured. Add clientId and authority in public/config.js");
        setIsAuthReady(true);
        return;
      }

      try {
        await msalClient.initialize();
        await msalClient.handleRedirectPromise();
        const accounts = msalClient.getAllAccounts();
        const activeAccount = msalClient.getActiveAccount() || accounts[0] || null;

        if (activeAccount) {
          msalClient.setActiveAccount(activeAccount);

          const verification = await verifyBackendAccess(activeAccount);
          if (!verification.ok) {
            window.localStorage.removeItem(AUTH_STORAGE_KEY);
            setSession({ isAuthenticated: false, user: null });
            setAppAccessDenied({ message: verification.message });
            setIsAuthReady(true);
            return;
          }

          const nextSession = { isAuthenticated: true, user: buildUserFromAccount(activeAccount) };
          window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
          setSession(nextSession);
        } else {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
          setSession({ isAuthenticated: false, user: null });
        }
      } catch (error) {
        console.warn("MSAL init failed", error);
        setAuthError(`Unable to initialize SSO. ${parseMsalError(error)}`);
      } finally {
        setIsAuthReady(true);
      }
    };

    initializeAuth();
  }, [msalClient, verifyBackendAccess]);

  const login = useCallback(async () => {
    if (!msalClient) {
      setAuthError("SSO is not configured. Add clientId and authority in public/config.js");
      setAutoLoginAttempted(true);
      return;
    }

    setIsAuthenticating(true);
    setAuthError(null);

    try {
      const response = await msalClient.loginPopup({
        scopes: msalSettings.scopes,
        prompt: "select_account"
      });

      const account = response.account;
      if (account) {
        msalClient.setActiveAccount(account);

        const verification = await verifyBackendAccess(account);
        if (!verification.ok) {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
          setSession({ isAuthenticated: false, user: null });
          setAppAccessDenied({ message: verification.message });
          setAutoLoginAttempted(true);
          setIsAuthenticating(false);
          return;
        }

        const nextSession = { isAuthenticated: true, user: buildUserFromAccount(account) };
        window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession));
        setSession(nextSession);
        setAutoLoginAttempted(true);
      }
    } catch (error) {
      console.warn("MSAL login failed", error);
      setAuthError(`SSO login failed. ${parseMsalError(error)}`);
      setAutoLoginAttempted(true);
    } finally {
      setIsAuthenticating(false);
    }
  }, [msalClient, verifyBackendAccess]);

  // Automatically pop the MSAL login window once on load if no session exists.
  useEffect(() => {
    if (!isAuthReady || !msalSettings.isConfigured || session.isAuthenticated || isAuthenticating || autoLoginAttempted || appAccessDenied) {
      return;
    }
    login();
  }, [isAuthReady, session.isAuthenticated, isAuthenticating, autoLoginAttempted, appAccessDenied, login]);

  const logout = useCallback(async () => {
    if (msalClient) {
      try {
        const account = msalClient.getActiveAccount() || msalClient.getAllAccounts()[0] || undefined;
        await msalClient.logoutPopup({ account });
      } catch (error) {
        console.warn("MSAL logout failed", error);
      }
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    setSession({ isAuthenticated: false, user: null });
    setAppAccessDenied(null);
    setAutoLoginAttempted(false);
  }, [msalClient]);

  const value = useMemo(
    () => ({
      isAuthReady,
      isAuthenticating,
      isAuthenticated: session.isAuthenticated,
      user: session.user,
      authError,
      appAccessDenied,
      isMsalConfigured: msalSettings.isConfigured,
      login,
      logout
    }),
    [isAuthReady, isAuthenticating, session, authError, appAccessDenied, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
